import { useEffect, useMemo, useRef } from 'preact/hooks';
import type { WidgetConfig } from '../lib/types';
import { createChatSessionState } from '../lib/chat-session';
import { createChatController } from '../lib/chat-controller';
import type { Template, TemplateProps } from '../templates/types';
import { createPreviewApiClient, registerWidgetApiClient } from '../lib/api';
import { createTurnstileGate } from '../lib/turnstile-gate';
import type { TurnstileGate } from '../lib/turnstile-gate';
import { leadStorageKey } from '../lib/storage';

interface AppProps {
  siteId: string;
  apiBase: string;
  template: Template;
  initialConfig?: { config: WidgetConfig; token: string };
  /** Set to true only when running inside the CRM dashboard preview iframe. */
  isPreview?: boolean;
}

export function App({ siteId, apiBase, template: T, initialConfig, isPreview }: AppProps): preact.JSX.Element {
  const stateRef = useRef(createChatSessionState());
  const state = stateRef.current;

  const leadSubmittedRef = useRef(false);

  // The Turnstile gate is created once per App mount (after config is resolved)
  // using a ref so it survives re-renders.
  const gateRef = useRef<TurnstileGate | null>(null);

  const controller = useMemo(() => createChatController({
    siteId,
    state,
    pageUrl: window.location.href,
    getTurnstileToken: async () => {
      const gate = gateRef.current;
      if (!gate) return '';
      return gate.getToken(!!state.conversationId.value);
    },
    forceChallenge: async () => {
      const gate = gateRef.current;
      if (!gate) return '';
      return gate.forceChallenge();
    },
  }), [siteId, state]);

  useEffect(() => {
    if (initialConfig) {
      if (isPreview) {
        // Preview mode — register the postMessage client so submitLead routes
        // through the dashboard iframe bridge instead of the HTTP API.
        registerWidgetApiClient(siteId, createPreviewApiClient());
      }
      // Normal embed: the HTTP client was already registered by index.tsx before
      // App was rendered — do NOT overwrite it here.
      state.config.value = initialConfig.config;
      state.token.value = initialConfig.token;
      state.messages.value = [];
      state.conversationId.value = null;
      state.isLoading.value = false;
      return;
    }

    controller.initialize()
      .catch(() => {
        state.isLoading.value = false;
        state.error.value = 'Failed to load widget';
      });
  }, [siteId, initialConfig]);

  // Start/stop the poll loop when the panel opens or closes
  useEffect(() => {
    if (state.isOpen.value) {
      controller.startPolling();
    } else {
      controller.stopPolling();
    }
  }, [state.isOpen.value]);

  const config = initialConfig ? initialConfig.config : state.config.value;

  if ((!initialConfig && state.isLoading.value) || !config) {
    return <></>;
  }

  // Gate is created here — after config is guaranteed non-null — so siteKey,
  // maxSkips, and position are always the real values, never undefined.
  if (!gateRef.current) {
    gateRef.current = createTurnstileGate({
      siteKey: config.turnstile_site_key,
      position: config.position ?? 'bottom-right',
    });
  }

  const accentColor = config.accent_color || '#6366f1';
  const position = config.position || 'bottom-right';

  const templateProps: TemplateProps = {
    config,
    siteId,
    messages: state.messages.value,
    isSending: state.isSending.value,
    streamContent: state.currentStreamContent.value,
    error: state.error.value,
    accentColor,
    position,
    isOpen: state.isOpen.value,
    apiBase,
    token: state.token.value,
    controller: state.controller.value,
    onSend: config.lead_capture?.enabled === true
      ? (msg: string) => {
          if (!leadSubmittedRef.current) {
            try {
              if (!sessionStorage.getItem(leadStorageKey(siteId))) {
                state.isOpen.value = true;
                return;
              }
            } catch { /* sessionStorage unavailable — proceed to send */ }
          }
          controller.send(msg);
        }
      : controller.send,
    onClose: () => { state.isOpen.value = false; },
    onOpen: () => { state.isOpen.value = true; },
    onClear: controller.clear,
    onRetry: controller.retry,
    onInteraction: controller.recordInteraction,
    onLeadConversation: (conversationId: string, isRestore?: boolean) => {
      if (!isRestore) {
        controller.clear();
      }
      state.conversationId.value = conversationId;
      leadSubmittedRef.current = true;
      // Build and start the poller for the newly adopted conversation. The isOpen
      // effect fired before conversationId was set (it was null then), so polling
      // was never started for this conversation. startPolling() is a no-op if the
      // poller is already running for the same conversation.
      if (state.isOpen.value) {
        controller.startPolling();
      }
    },
  };

  return (
    <>
      <style>{T.getStyles(accentColor, position)}</style>
      {!state.isOpen.value && <T.Trigger {...templateProps} />}
      {state.isOpen.value && <T.Panel {...templateProps} />}
    </>
  );
}
